// AR Report Cohort Detail (Level 1)
//
// Single cohort × publication detail page. Shows:
//   - cohort lifecycle / grade
//   - 8 KPIs (success, retry recovery, fast-retry recovery, terminal loss, etc.)
//   - retry-path split summary (fast vs standard cohorts)
//   - Final disposition (clickable to bucket pages)
//   - Cycle effectiveness (per-attempt outcomes table)
//   - Daily wave timeline (when did each attempt wave fire?)
//   - Failure-category breakdown with recovery guidance
//   - Top failure detail strings (raw text from SC)
//
// Every metric, badge, and column has a hover tooltip explaining what it
// means and how to act on it.

import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  api,
  type ARCohortDetail,
  type ARCohortTimeline,
  type ARGrade,
  type ARFailureCategory,
} from '../api/client'
import { fmtInt } from '../lib/format'
import {
  Hint, StatusBadge, FailureCategoryBadge, LifecycleBadge,
} from '../components/ARGlossary'
import { ARWaveTimeline } from '../components/ARWaveTimeline'

const GRADE_TONE: Record<ARGrade, string> = {
  'A': 'text-radar-green',
  'B': 'text-paper',
  'C': 'text-amber-glow',
  'D': 'text-radar-red',
  '—': 'text-paper-trace',
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(n)
}

function fmtPct(p: number): string {
  return `${(p * 100).toFixed(1)}%`
}

function monthLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

export function ARReportCohortPage() {
  const { month, publication } = useParams<{ month: string; publication: string }>()
  const cohortMonth = month ? `${month}-01` : ''

  const detail = useQuery({
    queryKey: ['ar-cohort-detail', cohortMonth, publication],
    queryFn: () => api.get<ARCohortDetail>(
      `/api/ar-report/cohort/${cohortMonth}/${publication}`,
    ),
    enabled: !!cohortMonth && !!publication,
  })

  const timeline = useQuery({
    queryKey: ['ar-cohort-timeline', cohortMonth, publication],
    queryFn: () => api.get<ARCohortTimeline>(
      `/api/ar-report/cohort/${cohortMonth}/${publication}/timeline`,
    ),
    enabled: !!cohortMonth && !!publication,
  })

  if (detail.isLoading) return <div className="p-8 label text-paper-trace">Loading…</div>
  if (!detail.data) return <div className="p-8 label text-paper-trace">Cohort not found.</div>
  const data = detail.data

  const totalFailures = Object.values(data.failure_categories).reduce((a, b) => a + (b || 0), 0)
  const unrecoverable = new Set(data.failure_categories_unrecoverable || [])
  const failureCats = (Object.keys(data.failure_categories) as ARFailureCategory[])
    .filter((k) => (data.failure_categories[k] || 0) > 0)
    .sort((a, b) => (data.failure_categories[b] || 0) - (data.failure_categories[a] || 0))
  // Split rather than sorted together: a retry can never fix these, so they
  // are a different action (ask for a new card), not a worse decline.
  const deadEndCats = failureCats.filter((c) => unrecoverable.has(c))
  const retryableCats = failureCats.filter((c) => !unrecoverable.has(c))

  const renderFailureCard = (cat: ARFailureCategory, deadEnd: boolean) => {
    const subs = data.failure_categories[cat] || 0
    const attempts = data.failure_category_attempts?.[cat] || 0
    const pct = totalFailures ? subs / totalFailures : 0
    return (
      <Hint key={cat} section="failure_categories" termKey={cat} underline="none">
        <div
          className={`border p-3 cursor-help w-full ${
            deadEnd ? 'border-rust bg-rust/5' : 'border-ink-500 bg-ink-800/40'
          }`}
        >
          <div className="flex items-baseline justify-between">
            <FailureCategoryBadge category={cat} />
            <div className="flex items-baseline gap-1.5">
              <div
                className={`font-mono text-[18px] tabular-nums ${
                  deadEnd ? 'text-rust' : 'text-amber-glow'
                }`}
              >
                {fmtInt(subs)}
              </div>
              <div className="label text-paper-trace">members</div>
            </div>
          </div>
          <div className="bg-ink-700 h-1.5 mt-2">
            <div
              className={`h-full ${deadEnd ? 'bg-rust' : 'bg-amber-glow'}`}
              style={{ width: `${pct * 100}%` }}
            />
          </div>
          <div className="text-[11px] text-paper-trace font-mono mt-1">
            {fmtPct(pct)} of failing members
            {attempts > 0 && <> · {fmtInt(attempts)} attempts burned</>}
          </div>
        </div>
      </Hint>
    )
  }
  const fastRetryEligible = data.kpis.fast_retry_a1_count > 0

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3">
        <Link to="/ar-report" className="label-amber hover:underline">← AR Report</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">{monthLabel(data.cohort_month)} · {data.publication_name}</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">Cohort detail</div>
          <h1 className="font-serif text-[36px] leading-tight text-paper">
            {monthLabel(data.cohort_month)}
            <span className="text-amber-glow"> ∙ </span>
            <span className="italic font-light">{data.publication_name}</span>
          </h1>
          <div className="text-paper-dim text-[13px] mt-2 font-mono flex items-baseline gap-3 flex-wrap">
            <LifecycleBadge state={data.lifecycle_state} />
            <span>·</span>
            <Hint title="Cohort size" short="Number of AR-eligible subscribers in this cycle.">
              <span>{fmtInt(data.cohort_size)} subs</span>
            </Hint>
            <span>·</span>
            <Hint title="Revenue at stake" short="Cohort size × current renewal price. Captured if all subs renew, lost if all fail.">
              <span>{fmtCurrency(data.cohort_revenue)} at stake</span>
            </Hint>
          </div>
        </div>
        <div className="text-center">
          <div className="label text-paper-faint">
            <Hint title="Cohort grade" short="A/B/C/D based on final renewal rate. Only assigned after cohort settles (Day 31+).">
              Grade
            </Hint>
          </div>
          <div className={`font-mono text-[64px] font-bold leading-none ${GRADE_TONE[data.grade]}`}>{data.grade}</div>
          <div className="label text-paper-trace mt-1">{fmtPct(data.kpis.renewed_pct)} renewed</div>
        </div>
      </header>

      {/* KPIs */}
      <section className="space-y-2">
        <h2 className="font-serif text-[18px] text-paper">Key metrics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI
            label="First-attempt success"
            value={fmtPct(data.kpis.first_attempt_success_pct)}
            tone="text-radar-green"
            hintKey="first_attempt_success_pct"
          />
          <KPI
            label="Retry recovery"
            value={fmtPct(data.kpis.retry_recovery_pct)}
            sub="of failed-A1, recovered later"
            tone="text-amber-glow"
            hintKey="retry_recovery_pct"
          />
          <KPI
            label="Renewed (final)"
            value={fmtPct(data.kpis.renewed_pct)}
            sub={`${fmtInt(data.final_disposition.renewed.count)} subs`}
            tone="text-radar-green"
            hintKey="renewed_pct"
          />
          <KPI
            label="Terminal loss"
            value={fmtPct(data.kpis.terminal_loss_pct)}
            sub={`${fmtInt(data.final_disposition.terminal_fail.count)} subs`}
            tone="text-radar-red"
            hintKey="terminal_loss_pct"
          />
        </div>
      </section>

      {/* Retry-path split summary */}
      <section className="space-y-2">
        <h2 className="font-serif text-[18px] text-paper inline-flex items-baseline gap-2">
          Retry-path split
          <Hint
            title="Two retry paths"
            short="SC routes failures to two different cadences."
            detail="When SC fails to charge a card, it picks one of two retry paths based on the failure reason. The fast-retry path (status=CHARGE FAILED, no FIRST prefix) gets a +22h bonus retry on top of the standard schedule; only invalid-card-number failures use this path. Everything else uses the standard +5/+3/+8/+6 cadence with no fast-retry."
          >
            <span className="label text-paper-trace">what is this?</span>
          </Hint>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-radar-cyan/30 bg-ink-800/40 p-4">
            <div className="label text-paper-faint mb-1">
              <Hint section="retry_paths" termKey="fast">⚡ Fast-retry path</Hint>
            </div>
            <div className="font-mono text-[24px] tabular-nums text-radar-cyan">
              {fmtInt(data.retry_path_totals.fast)}
              <span className="text-[12px] text-paper-trace ml-2">attempts</span>
            </div>
            {fastRetryEligible && (
              <div className="text-[11px] text-paper-trace font-mono mt-1.5 space-y-0.5">
                <div>{fmtInt(data.kpis.fast_retry_a1_count)} subs took this path</div>
                <div className="text-radar-green">
                  ✓ {fmtInt(data.kpis.fast_retry_recovered_count)} recovered at +22h
                  ({fmtPct(data.kpis.fast_retry_recovery_pct)})
                </div>
                <div>= {fmtCurrency(data.kpis.fast_retry_recovered_revenue)} saved</div>
              </div>
            )}
          </div>
          <div className="border border-ink-500 bg-ink-800/40 p-4">
            <div className="label text-paper-faint mb-1">
              <Hint section="retry_paths" termKey="standard">Standard cadence</Hint>
            </div>
            <div className="font-mono text-[24px] tabular-nums text-paper">
              {fmtInt(data.retry_path_totals.standard)}
              <span className="text-[12px] text-paper-trace ml-2">attempts</span>
            </div>
            <div className="text-[11px] text-paper-trace font-mono mt-1.5">
              +5/+3/+8/+6 day schedule
            </div>
          </div>
          <div className="border border-ink-500 bg-ink-800/40 p-4">
            <div className="label text-paper-faint mb-1">
              <Hint
                title="Successes"
                short="CHARGED + CARD UPDATED rows. No retry path needed."
                detail="retry_path is NULL on non-failure rows. These count CHARGED (renewals captured) and CARD UPDATED (customer self-service card change)."
              >
                Non-failures
              </Hint>
            </div>
            <div className="font-mono text-[24px] tabular-nums text-radar-green">
              {fmtInt(data.retry_path_totals.null)}
              <span className="text-[12px] text-paper-trace ml-2">attempts</span>
            </div>
            <div className="text-[11px] text-paper-trace font-mono mt-1.5">
              CHARGED + CARD UPDATED rows
            </div>
          </div>
        </div>
      </section>

      {/* Final disposition — clickable bucket cards */}
      <section className="space-y-3">
        <h2 className="font-serif text-[18px] text-paper inline-flex items-baseline gap-2">
          Final disposition
          <Hint
            title="Final disposition"
            short="Where each subscriber ended up after SC ran the full cycle."
            detail="Click any bucket to see the subscribers in it. Renewed = success. Charge pending = charged, the gateway's answer isn't back yet. In retry = still has scheduled attempts pending. Terminal fail = exhausted all 5 retries. Awaiting = no attempt fired yet (or card just updated). AR disabled = excluded from the AR cohort."
          >
            <span className="label text-paper-trace">what is this?</span>
          </Hint>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {(['renewed', 'pending', 'in_retry', 'terminal_fail', 'awaiting', 'ar_disabled'] as const).map((b) => {
            const v = data.final_disposition[b] ?? { count: 0, revenue: 0 }
            const linkTo = `/ar-report/${month}/${publication}/bucket/${b}`
            return (
              <Link key={b} to={linkTo}
                    className="border border-ink-500 bg-ink-800/40 p-4 hover:bg-ink-700/40 transition-colors">
                <div className="label text-paper-faint">
                  <Hint section="buckets" termKey={b}>{b.replace('_', ' ')}</Hint>
                </div>
                <div className="font-mono text-[24px] tabular-nums text-paper">{fmtInt(v.count)}</div>
                <div className="text-[11px] text-paper-trace font-mono mt-1">{fmtCurrency(v.revenue)}</div>
              </Link>
            )
          })}
        </div>
      </section>

      {/* Daily wave timeline */}
      {timeline.data && (
        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-[18px] text-paper">Daily wave timeline</h2>
            <Link
              to={`/ar-report/${month}/${publication}/attempts`}
              className="label-amber text-[11px] hover:underline"
            >
              See all attempts →
            </Link>
          </div>
          <ARWaveTimeline
            data={timeline.data}
            monthParam={month ?? ''}
            publication={publication ?? ''}
          />
        </section>
      )}

      {/* Cycle effectiveness — per-attempt with retry path split */}
      <section className="space-y-3">
        <h2 className="font-serif text-[18px] text-paper inline-flex items-baseline gap-2">
          Per-attempt cycle effectiveness
          <Hint
            title="Cycle effectiveness"
            short="How each attempt-in-cycle performed."
            detail="One row per attempt seq (A1=first scheduled charge, A2=first retry, etc.). Outcomes show what happened on that attempt. The retry_path column shows whether subs went on the fast or standard path."
          >
            <span className="label text-paper-trace">what is this?</span>
          </Hint>
        </h2>
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-[13px] font-mono">
            <thead>
              <tr className="border-b border-ink-500/60 bg-ink-800/60">
                <th className="label py-2 px-4 font-normal text-left">Attempt</th>
                <th className="label py-2 px-3 font-normal text-right">Subs entered</th>
                <th className="label py-2 px-3 font-normal text-left">Outcomes</th>
                <th className="label py-2 px-3 font-normal text-left">Retry path</th>
                <th className="label py-2 px-3 font-normal text-left">Top failure reasons</th>
              </tr>
            </thead>
            <tbody>
              {data.stages.map((stage) => (
                <tr key={stage.attempt_seq} className="border-b border-ink-500/30">
                  <td className="px-4 py-3 text-paper font-bold align-top">A{stage.attempt_seq}</td>
                  <td className="px-3 py-3 text-right tabular-nums text-paper align-top">{fmtInt(stage.entered)}</td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(stage.outcomes).map(([status, v]) => (
                        <span key={status} className="inline-flex items-center gap-1">
                          <StatusBadge status={status} />
                          <span className="tabular-nums text-paper-dim text-[11px]">×{v.count}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex flex-wrap gap-2">
                      {stage.by_retry_path.fast > 0 && (
                        <span className="text-radar-cyan tabular-nums text-[11px]">⚡{stage.by_retry_path.fast}</span>
                      )}
                      {stage.by_retry_path.standard > 0 && (
                        <span className="text-paper-dim tabular-nums text-[11px]">std·{stage.by_retry_path.standard}</span>
                      )}
                      {stage.by_retry_path.null > 0 && (
                        <span className="text-radar-green tabular-nums text-[11px]">✓{stage.by_retry_path.null}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(stage.by_failure_category)
                        .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
                        .slice(0, 3)
                        .map(([cat, n]) => (
                          <span key={cat} className="inline-flex items-center gap-1">
                            <FailureCategoryBadge category={cat} />
                            <span className="tabular-nums text-paper-trace text-[10px]">×{n}</span>
                          </span>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
              {data.stages.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-paper-trace label">No attempts observed in this cohort window yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Failure category breakdown */}
      {totalFailures > 0 && (
        <section className="space-y-3">
          <h2 className="font-serif text-[18px] text-paper inline-flex items-baseline gap-2">
            Why cards failed
            <Hint
              title="Failure category breakdown"
              short="Subscribers in each failure reason. SC retries up to 5x, so attempt counts are much higher."
              detail="Counts are SUBSCRIBERS, not attempts — the attempt figure is shown beneath each card. 'No card on file' is separated because a retry can never fix it: Authorize.Net's Card Account Updater asked the issuer, was told the account is closed with no replacement, and deleted the stored card. Those members must supply a new card. Everything else can and often does succeed on a later attempt."
            >
              <span className="label text-paper-trace">what is this?</span>
            </Hint>
          </h2>
          {deadEndCats.length > 0 && (
            <div className="space-y-2">
              <div className="label text-rust">
                Needs a new card — retrying cannot fix these
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {deadEndCats.map((cat) =>
                  renderFailureCard(cat, true),
                )}
              </div>
            </div>
          )}
          {retryableCats.length > 0 && (
            <div className="space-y-2">
              <div className="label text-paper-trace">
                May recover on a later attempt
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {retryableCats.map((cat) => renderFailureCard(cat, false))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Top failure detail strings */}
      {data.top_failure_details.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-serif text-[18px] text-paper inline-flex items-baseline gap-2">
            Top decline reasons (raw text)
            <Hint
              title="Raw decline reasons"
              short="Top 5 free-text decline messages from SC, by members affected."
              detail="The exact text SC's report included for each failure. We use these strings to derive failure_category. New patterns here may need a classifier rule update."
            >
              <span className="label text-paper-trace">what is this?</span>
            </Hint>
          </h2>
          <div className="border border-ink-500 bg-ink-800/40">
            {data.top_failure_details.map((d, i) => (
              <div key={i} className="px-4 py-2 border-b border-ink-500/30 last:border-0 flex justify-between gap-3">
                <span className="font-mono text-[12px] text-paper-dim">{d.details}</span>
                <span className="font-mono text-[12px] tabular-nums shrink-0 flex items-baseline gap-1.5">
                  <span className="text-amber-glow">{fmtInt(d.subscribers)}</span>
                  <span className="label text-paper-trace">members</span>
                  <span className="text-paper-trace">· {fmtInt(d.count)} attempts</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function KPI({
  label, value, sub, tone, hintKey,
}: {
  label: string
  value: string
  sub?: string
  tone?: string
  hintKey?: string
}) {
  return (
    <div className="border border-ink-500 bg-ink-800/40 p-4">
      <div className="label text-paper-faint">
        {hintKey ? <Hint section="kpis" termKey={hintKey}>{label}</Hint> : label}
      </div>
      <div className={`font-mono text-[26px] tabular-nums ${tone || 'text-paper'}`}>{value}</div>
      {sub && <div className="text-[11px] text-paper-trace font-mono mt-1">{sub}</div>}
    </div>
  )
}
